import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './Subscribe.css';

declare global {
  interface Window {
    paypal: any;
  }
}

const Subscribe: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  const [activeTab, setActiveTab] = useState<'paypal' | 'crypto'>('paypal');
  const [txid, setTxid] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [status, setStatus] = useState<{ type: 'waiting' | 'success' | 'error'; message: string }>({ 
    type: 'waiting', 
    message: 'Awaiting payment' 
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [daysAdded, setDaysAdded] = useState(30);
  const [paypalLoaded, setPaypalLoaded] = useState(false);
  
  // Payment config - these should come from your backend in production
  const PRICE = '9.99';
  const CURRENCY = 'EUR';
  const USDT_ADDRESS = 'TYPmiDKmqhBFRw5Y7K8jnWYuqvjdoM5sXo';

  // Check authentication
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login?redirect=/subscribe');
    }
  }, [isAuthenticated, navigate]);

  // Check existing subscription
  useEffect(() => {
    const checkSubscription = async () => {
      const token = localStorage.getItem('auth_token');
      if (!token) return;

      try {
        const response = await fetch('/api/payment/subscription-status', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        
        if (data.status === 'success' && data.data?.has_subscription) {
          const end = new Date(data.data.subscription.current_period_end);
          const days = Math.ceil((end.getTime() - Date.now()) / 86400000);
          setStatus({ type: 'success', message: `Active Subscription — ${days} days remaining` });
        }
      } catch (error) {
        console.error('Failed to check subscription:', error);
      }
    };

    checkSubscription();
  }, []);

  // Load PayPal SDK
  useEffect(() => {
    if (paypalLoaded || !isAuthenticated) return;

    const script = document.createElement('script');
    script.src = `https://www.paypal.com/sdk/js?client-id=AYPZSYtHBQQUhEBJnIvB_gR5wPzfNIVqvLLKPvv2GC3YKSJ5kR5-lD6TFhAV2ufqoVhGI3Qe4jh6VVKX&currency=EUR&components=buttons,funding-eligibility`;
    script.async = true;
    script.onload = () => {
      setPaypalLoaded(true);
      initPayPal();
    };
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, [isAuthenticated, paypalLoaded]);

  const initPayPal = () => {
    if (!window.paypal) return;

    const token = localStorage.getItem('auth_token');
    if (!token) return;

    // PayPal button
    window.paypal.Buttons({
      style: { layout: 'vertical', color: 'blue', shape: 'rect', height: 45 },
      fundingSource: window.paypal.FUNDING.PAYPAL,
      createOrder: (_data: any, actions: any) => actions.order.create({
        purchase_units: [{ amount: { value: PRICE }, description: 'CineClubs Premium' }]
      }),
      onApprove: async (_data: any, actions: any) => {
        const details = await actions.order.capture();
        const response = await fetch('/api/payment/paypal-verify.php', {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`, 
            'Content-Type': 'application/json' 
          },
          body: JSON.stringify({ 
            order_id: _data.orderID, 
            payer_id: details.payer.payer_id, 
            amount: details.purchase_units[0].amount.value 
          })
        });
        const result = await response.json();
        if (result.status === 'success') {
          setDaysAdded(result.data?.days_added || 30);
          setShowSuccess(true);
        }
      }
    }).render('#paypal-button-container');

    // Card button
    if (window.paypal.FUNDING.CARD) {
      window.paypal.Buttons({
        style: { layout: 'vertical', color: 'white', shape: 'rect', height: 45 },
        fundingSource: window.paypal.FUNDING.CARD,
        createOrder: (_data: any, actions: any) => actions.order.create({
          purchase_units: [{ amount: { value: PRICE }, description: 'CineClubs Premium' }]
        }),
        onApprove: async (_data: any, actions: any) => {
          const details = await actions.order.capture();
          const response = await fetch('/api/payment/paypal-verify.php', {
            method: 'POST',
            headers: { 
              'Authorization': `Bearer ${token}`, 
              'Content-Type': 'application/json' 
            },
            body: JSON.stringify({ 
              order_id: _data.orderID, 
              payer_id: details.payer ? details.payer.payer_id : 'card', 
              amount: details.purchase_units[0].amount.value 
            })
          });
          const result = await response.json();
          if (result.status === 'success') {
            setDaysAdded(result.data?.days_added || 30);
            setShowSuccess(true);
          }
        }
      }).render('#paypal-button-container');
    }
  };

  const copyAddress = () => {
    navigator.clipboard.writeText(USDT_ADDRESS);
  };

  const verifyPayment = async () => {
    if (!txid || txid.length < 60) {
      setStatus({ type: 'error', message: 'Invalid transaction hash' });
      return;
    }

    setVerifying(true);
    setStatus({ type: 'waiting', message: 'Checking blockchain...' });

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/payment/verify-txid.php', {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`, 
          'Content-Type': 'application/json' 
        },
        body: JSON.stringify({ txid, wallet_address: USDT_ADDRESS })
      });

      const data = await response.json();
      
      if (data.status === 'success') {
        setStatus({ type: 'success', message: `Payment confirmed! +${data.data?.days_added || 30} days added` });
        setTimeout(() => {
          setDaysAdded(data.data?.days_added || 30);
          setShowSuccess(true);
        }, 1000);
      } else {
        setStatus({ type: 'error', message: data.message || 'Verification failed' });
      }
    } catch (error) {
      setStatus({ type: 'error', message: 'Connection error. Please try again.' });
    } finally {
      setVerifying(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="subscribe-page">
        <div className="subscribe-container">
          <div className="subscribe-card">
            <div className="card-body">
              <div className="login-prompt">
                <div className="icon">🔐</div>
                <p>Please sign in to subscribe</p>
                <a href="/login?redirect=/subscribe" className="btn-primary">Sign In</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="subscribe-page">
      <div className="subscribe-container">
        <div className="subscribe-card">
          {/* Header */}
          <div className="card-header">
            <div className="header-row">
              <div className="brand-logo">
                <img src="/public/assets/images/paylogo.png" alt="CineClubs" />
                <span className="brand-name">Cine<span>Clubs</span></span>
              </div>
              <div className="plan-summary">
                <span className="plan-label">Premium</span>
                <span className="plan-price">€{PRICE}<small>{CURRENCY}</small></span>
                <span className="plan-period">/ 30 Days</span>
                <span className="plan-features">HD/4K • Watch Parties • Live Streams • Ad-Free</span>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="card-body">
            <div className="payment-section">
              {/* Payment Tabs */}
              <div className="pay-tabs">
                <div className={`tab-slider ${activeTab}`}></div>
                <button
                  className={`pay-tab ${activeTab === 'paypal' ? 'active' : ''}`}
                  onClick={() => setActiveTab('paypal')}
                >
                  <i className="fab fa-paypal"></i> PayPal / Card
                </button>
                <button
                  className={`pay-tab ${activeTab === 'crypto' ? 'active' : ''}`}
                  onClick={() => setActiveTab('crypto')}
                >
                  <i className="fab fa-bitcoin"></i> Crypto
                </button>
              </div>

              {/* PayPal Content */}
              {activeTab === 'paypal' && (
                <div className="pay-content active">
                  <div id="paypal-button-container"></div>
                  
                  <div className="security-badge">
                    <div className="security-item"><i className="fas fa-shield-alt"></i> SSL</div>
                    <div className="security-item"><i className="fas fa-lock"></i> Encrypted</div>
                    <div className="security-item"><i className="fas fa-check-circle"></i> Verified</div>
                  </div>
                </div>
              )}

              {/* Crypto Content */}
              {activeTab === 'crypto' && (
                <div className="pay-content active">
                  <div className="crypto-box">
                    <div className="crypto-label">USDT TRC-20 Address</div>
                    <div className="wallet-row">
                      <code>{USDT_ADDRESS}</code>
                      <button className="copy-btn" onClick={copyAddress}>
                        <i className="fas fa-copy"></i>
                      </button>
                    </div>
                    <div className="crypto-label">Transaction Hash</div>
                    <div className="txid-row">
                      <input
                        type="text"
                        className="txid-input"
                        placeholder="Paste TXID..."
                        value={txid}
                        onChange={(e) => setTxid(e.target.value)}
                      />
                      <button
                        className="verify-btn"
                        onClick={verifyPayment}
                        disabled={verifying}
                      >
                        {verifying ? <i className="fas fa-spinner fa-spin"></i> : 'Verify'}
                      </button>
                    </div>
                    <div className={`status-msg ${status.type}`}>
                      <i className={`fas fa-${status.type === 'waiting' ? 'clock' : status.type === 'success' ? 'check-circle' : 'times-circle'}`}></i>
                      {' '}{status.message}
                    </div>
                  </div>
                </div>
              )}

              {/* Trust Footer */}
              <div className="trust-footer">
                <div className="trust-text">
                  <i className="fas fa-shield-alt"></i> Your payment is secure and encrypted
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccess && (
        <div className="success-overlay show">
          <div className="success-modal">
            <div className="icon">🎉</div>
            <h2>Subscription Activated!</h2>
            <div className="days">+{daysAdded} Days</div>
            <p>Enjoy Premium access</p>
            <button onClick={() => navigate('/movies')}>Start Watching</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Subscribe;
